import React, { useState } from 'react';
import { SimulationResponse } from '../types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, AreaChart, Area } from 'recharts';
import './ResultsDisplay.css';

interface ResultsDisplayProps {
  results: SimulationResponse;
}

export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ results }) => {
  const { request, results: simulationResults, metadata } = results;
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatSuccessRate = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Prepare data for charts
  const historicalData = simulationResults.historical.details.map(detail => ({
    period: `${detail.startYear}-${detail.endYear}`,
    success: detail.failed ? 0 : 1,
    balance: detail.terminalBalance,
    failed: detail.failed ? 1 : 0
  }));



  // Enhanced data for better charts
  const enhancedHistoricalData = simulationResults.historical.details.map(detail => ({
    period: `${detail.startYear}-${detail.endYear}`,
    startYear: detail.startYear,
    success: detail.failed ? 0 : 1,
    balance: detail.terminalBalance,
    failed: detail.failed ? 1 : 0,
    outcome: detail.failed ? 0 : 1,  // Single outcome value for the chart
    barHeight: 1, // Constant height for all bars
    withdrawalAmount: request.startingBalance * (request.withdrawalRate / 100),
    finalBalance: detail.terminalBalance,
    yearsSurvived: detail.failed ? (detail.failureYear ? detail.failureYear - detail.startYear + 1 : request.years) : request.years,
    failureYear: detail.failureYear
  }));



  // Generate Monte Carlo distribution data from actual simulation results
  const generateMonteCarloDistribution = () => {
    if (!simulationResults.monteCarlo.results || simulationResults.monteCarlo.results.length === 0) return [];
    
    // Use the actual terminal balances from Monte Carlo runs
    const balances = simulationResults.monteCarlo.results;
    
    // Create histogram bins for the actual data
    const binCount = 50; // Number of bins for smooth visualization
    const minBalance = Math.min(...balances);
    const maxBalance = Math.max(...balances);
    const binSize = (maxBalance - minBalance) / binCount;
    
    // Initialize bins
    const bins = new Array(binCount).fill(0);
    
    // Count occurrences in each bin
    balances.forEach(balance => {
      const binIndex = Math.min(Math.floor((balance - minBalance) / binSize), binCount - 1);
      bins[binIndex]++;
    });
    
    // Convert bins to chart data
    const data = [];
    for (let i = 0; i < binCount; i++) {
      const balance = minBalance + i * binSize + binSize / 2; // Center of bin
      const frequency = bins[i];
      
      if (frequency > 0) {
        data.push({
          balance: Math.round(balance),
          frequency: frequency
        });
      }
    }
    
    return data;
  };

  const monteCarloDistributionData = generateMonteCarloDistribution();
  
  // Debug logging
  console.log('Monte Carlo Data:', {
    hasResults: !!simulationResults.monteCarlo.results,
    resultsLength: simulationResults.monteCarlo.results?.length || 0,
    hasStatistics: !!simulationResults.monteCarlo.statistics,
    statistics: simulationResults.monteCarlo.statistics,
    successRate: simulationResults.monteCarlo.successRate,
    runs: simulationResults.monteCarlo.runs,
    generatedData: monteCarloDistributionData,
    dataLength: monteCarloDistributionData.length
  });
  
  // Fallback data if generation fails - simple bell curve
  const fallbackData = [];
  const { meanEndBalance, standardDeviation } = simulationResults.monteCarlo.statistics || {};
  
  if (meanEndBalance && standardDeviation) {
    // First add failures at $0
    const failureRate = 1 - (simulationResults.monteCarlo.successRate / 100);
    if (failureRate > 0) {
      const failureCount = Math.round(failureRate * simulationResults.monteCarlo.runs);
      fallbackData.push({ balance: 0, frequency: failureCount });
    }
    
    // Then create a simple bell curve for successes
    for (let i = 0; i <= 30; i++) {
      const balance = Math.max(1000, meanEndBalance - 2 * standardDeviation) + (i * 3 * standardDeviation) / 30;
      const normalizedPos = i / 30; // 0 to 1
      const frequency = Math.round(simulationResults.monteCarlo.runs * 0.2 * Math.exp(-4 * (normalizedPos - 0.5) * (normalizedPos - 0.5)));
      
      if (frequency > 0 && balance > 0) {
        fallbackData.push({ balance: Math.round(balance), frequency });
      }
    }
  }
  
  const finalMonteCarloData = monteCarloDistributionData.length > 0 ? monteCarloDistributionData : fallbackData;

  // Generate recommendation
  const getRecommendation = () => {
    const historicalSuccess = simulationResults.historical.successRate;
    const monteCarloSuccess = simulationResults.monteCarlo.successRate;
    
    if (historicalSuccess >= 95 && monteCarloSuccess >= 95) {
      return {
        level: 'success',
        message: 'Excellent! This withdrawal strategy appears very safe.',
        icon: '🟢'
      };
    } else if (historicalSuccess >= 90 && monteCarloSuccess >= 90) {
      return {
        level: 'warning',
        message: 'Good! This strategy has a high probability of success.',
        icon: '🟡'
      };
    } else if (historicalSuccess >= 80 && monteCarloSuccess >= 80) {
      return {
        level: 'warning',
        message: 'Acceptable risk level, but consider adjustments for more safety.',
        icon: '🟡'
      };
    } else {
      return {
        level: 'danger',
        message: 'High risk of failure. Consider reducing withdrawal rate or adjusting portfolio.',
        icon: '🔴'
      };
    }
  };

  const recommendation = getRecommendation();

  return (
    <div className="results-display">
      <h2>📊 Simulation Results</h2>
      
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Portfolio</h3>
          <div className="card-value">
            {(request.portfolioRatio.equity * 100).toFixed(0)}% / {(request.portfolioRatio.bonds * 100).toFixed(0)}%
          </div>
          <div className="card-label">Equity / Bonds</div>
        </div>
        
        <div className="summary-card">
          <h3>Starting Withdrawal</h3>
          <div className="card-value">{request.withdrawalRate}%</div>
          <div className="card-label">{formatCurrency(request.startingBalance * (request.withdrawalRate / 100))}</div>
        </div>
        
        <div className="summary-card">
          <h3>Starting Balance</h3>
          <div className="card-value">{formatCurrency(request.startingBalance)}</div>
          <div className="card-label">Initial Portfolio</div>
        </div>
        
        <div className="summary-card">
          <h3>Time Horizon</h3>
          <div className="card-value">{request.years} years</div>
          <div className="card-label">Retirement Period</div>
        </div>
      </div>

      {/* Withdrawal Schedule */}
      <div className="withdrawal-schedule">
        <div className="schedule-header" onClick={() => setScheduleExpanded(!scheduleExpanded)}>
          <h3>💰 Hypothetical Withdrawal Schedule</h3>
          <span className="expand-icon">{scheduleExpanded ? '▼' : '▶'}</span>
        </div>
        {scheduleExpanded && (
          <div className="schedule-content">
            <p className="schedule-description">
              This table shows your portfolio withdrawals and total income for all {request.years} years. 
              Portfolio withdrawal is reduced by Social Security/pension income received.
              <br />
              <small style={{ color: '#6b7280', fontStyle: 'italic' }}>
                Portfolio withdrawal = desired spending - supplemental income. Tax estimates are simplified for display; actual simulation uses comprehensive tax rules.
              </small>
            </p>
            <div className="schedule-table-container">
              <table className="schedule-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Portfolio Withdrawal</th>
                    <th>Supplemental Income</th>
                    <th>Standard Deduction</th>
                    <th>Taxable Income</th>
                    <th>Total Taxes</th>
                    <th>Total Income</th>
                    <th>Annual Inflation Rate</th>
                    <th>Cumulative Inflation</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: request.years }, (_, i) => {
                    const year = i + 1;
                    const annualInflationRate = 0.027; // 2.7% average annual inflation
                    const cumulativeInflation = Math.pow(1 + annualInflationRate, i);
                    

                    
                    // Desired spending amount (inflation-adjusted)
                    const desiredSpending = request.startingBalance * (request.withdrawalRate / 100) * cumulativeInflation;
                    
                    // Supplemental income (Social Security, pension, etc.) with inflation adjustment
                    const supplementalIncome = (request.recurringIncome.socialSecurity + request.recurringIncome.pension + request.recurringIncome.other) * cumulativeInflation;
                    
                    // Portfolio withdrawal needed (desired spending minus supplemental income)
                    const portfolioWithdrawalNeeded = Math.max(0, desiredSpending - supplementalIncome);
                    
                    // Standard deduction (2024 single filer, indexed for inflation)
                    const standardDeduction = request.taxFree ? 0 : Math.round(14600 * cumulativeInflation);
                    
                    // Calculate taxes with proper standard deduction application
                    let totalTaxes = 0;
                    let taxableIncome = 0;
                    
                    if (!request.taxFree) {
                      // Total income before deductions
                      const totalIncomeBeforeDeduction = portfolioWithdrawalNeeded + supplementalIncome;
                      
                      // Apply standard deduction to total income
                      const taxableAfterDeduction = Math.max(0, totalIncomeBeforeDeduction - standardDeduction);
                      taxableIncome = taxableAfterDeduction;
                      
                      // Calculate taxes (simplified: 15% on portfolio, 12% on Social Security)
                      const portfolioTax = Math.round(Math.min(portfolioWithdrawalNeeded, taxableAfterDeduction) * 0.15);
                      const socialSecurityTax = Math.round(Math.max(0, taxableAfterDeduction - portfolioWithdrawalNeeded) * 0.12);
                      totalTaxes = portfolioTax + socialSecurityTax;
                      
                      // Debug logging for first few years
                      if (year <= 3) {
                        console.log(`Year ${year} Tax Debug:`, {
                          portfolioWithdrawal: portfolioWithdrawalNeeded,
                          supplementalIncome,
                          totalIncomeBeforeDeduction,
                          standardDeduction,
                          taxableAfterDeduction,
                          portfolioTax,
                          socialSecurityTax,
                          totalTaxes
                        });
                      }
                    }
                    
                    // Portfolio withdrawal needed
                    const totalPortfolioWithdrawal = portfolioWithdrawalNeeded;
                    
                    // Total income available to user
                    const totalIncome = desiredSpending;
                    
                    return (
                      <tr key={year}>
                        <td>{year}</td>
                        <td>{formatCurrency(totalPortfolioWithdrawal)}</td>
                        <td>{formatCurrency(supplementalIncome)}</td>
                        <td>{formatCurrency(standardDeduction)}</td>
                        <td>{formatCurrency(taxableIncome)}</td>
                        <td>{formatCurrency(totalTaxes)}</td>
                        <td>{formatCurrency(totalIncome)}</td>
                        <td>{formatPercentage(annualInflationRate)}</td>
                        <td>{formatPercentage(cumulativeInflation - 1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Success Rates Comparison */}
      <div className="success-rates">
        <h3>🎯 Success Rates</h3>
        <div className="rate-comparison">
          <div className="rate-item">
            <div className="rate-label">Historical Backtesting</div>
            <div className="rate-value success">{formatSuccessRate(simulationResults.historical.successRate)}</div>
            <div className="rate-detail">{simulationResults.historical.periods} periods tested</div>
          </div>
          
          <div className="rate-item">
            <div className="rate-label">Monte Carlo Simulation</div>
            <div className="rate-value success">{formatSuccessRate(simulationResults.monteCarlo.successRate)}</div>
            <div className="rate-detail">{formatNumber(simulationResults.monteCarlo.runs)} simulations</div>
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <div className={`recommendation ${recommendation.level}`}>
        <div className="recommendation-icon">{recommendation.icon}</div>
        <div className="recommendation-content">
          <h3>Recommendation</h3>
          <p>{recommendation.message}</p>
        </div>
      </div>

      {/* Monte Carlo Statistics */}
      <div className="monte-carlo-stats">
        <h3>📈 Monte Carlo Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-label">Median Outcome</div>
            <div className="stat-value">{formatCurrency(simulationResults.monteCarlo.statistics.medianEndBalance)}</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-label">5th Percentile</div>
            <div className="stat-value">{formatCurrency(simulationResults.monteCarlo.statistics.p5EndBalance)}</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-label">95th Percentile</div>
            <div className="stat-value">{formatCurrency(simulationResults.monteCarlo.statistics.p95EndBalance)}</div>
          </div>
        </div>
      </div>

      {/* Detailed Analysis Header */}
      <h3 className="detailed-analysis-header">📊 Detailed Analysis</h3>
      
      {/* Charts */}
      <div className="charts-section">
        
        {/* Portfolio Performance Over Time */}
        <div className="chart-container">
          <h4>Portfolio Balance Trajectories</h4>
                      <ResponsiveContainer width="100%" height={450}>
              <LineChart data={enhancedHistoricalData} margin={{ bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="startYear" 
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => value.toString()}
                label={{ value: 'Start Year', position: 'insideBottom', offset: -10 }}
                width={80}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                width={80}
                domain={['dataMin - 100000', 'dataMax + 100000']}
                label={{ value: 'Portfolio Balance', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value, name) => [formatCurrency(value as number), 'Balance']}
                labelFormatter={(label) => `Period: ${label}-${parseInt(label) + request.years - 1}`}
              />
              <Line 
                type="monotone" 
                dataKey="balance" 
                stroke="#667eea" 
                strokeWidth={2}
                dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                name="Portfolio Balance"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="chart-legend">
            <p><strong>X-Axis:</strong> Start year of each 30-year historical period</p>
            <p><strong>Y-Axis:</strong> Final portfolio balance after 30 years</p>
            <p><strong>Hover:</strong> See exact period and balance values</p>
          </div>
        </div>

        {/* Success/Failure Distribution */}
        <div className="chart-container">
          <h4>Historical Period Outcomes</h4>
                      <ResponsiveContainer width="100%" height={350}>
              <BarChart data={enhancedHistoricalData} margin={{ bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="startYear" 
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => value.toString()}
                label={{ value: 'Start Year', position: 'insideBottom', offset: -10 }}
                width={80}
              />
              <YAxis 
                label={{ value: 'Outcome', angle: -90, position: 'insideLeft' }}
                domain={[0, 1]}
                ticks={[0, 1]}
                tickFormatter={(value) => value === 1 ? 'Success' : 'Failure'}
              />
              <Tooltip 
                formatter={(value, name) => {
                  const entry = enhancedHistoricalData.find(d => d.startYear === parseInt(String(value)));
                  const isSuccess = entry && !entry.failed;
                  return [
                    <span style={{ color: isSuccess ? '#82ca9d' : '#ff6b6b', fontWeight: 'bold' }}>
                      {isSuccess ? 'Success' : 'Failure'}
                    </span>, 
                    'Outcome'
                  ];
                }}
                labelFormatter={(label) => `Period: ${label}-${parseInt(label) + request.years - 1}`}
              />
              <Bar 
                dataKey="barHeight" 
                fill="#82ca9d"
                name="Outcome"
              >
                {enhancedHistoricalData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`}
                    fill={entry.failed ? '#ff6b6b' : '#82ca9d'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="chart-legend">
            <p><strong>X-Axis:</strong> Start year of each 30-year historical period</p>
            <p><strong>Y-Axis:</strong> All bars have equal height (1) - colors indicate outcome</p>
            <p><strong>Colors:</strong> 🟢 Green = Success, 🔴 Red = Failure</p>
            <p><strong>Hover:</strong> See exact period and outcome with colored text</p>
          </div>
        </div>

        {/* Monte Carlo Distribution */}
        <div className="chart-container">
          <h4>Monte Carlo Terminal Balance Distribution</h4>
                      <ResponsiveContainer width="100%" height={450}>
              <LineChart data={finalMonteCarloData} margin={{ bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="balance" 
                tickFormatter={(value) => formatCurrency(value)}
                label={{ value: 'Terminal Balance', position: 'insideBottom', offset: -10 }}
              />
              <YAxis 
                label={{ value: 'Number of Scenarios', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value, name) => [value, 'Scenarios']}
                labelFormatter={(label) => `Balance: ${formatCurrency(label)}`}
              />
              <Line 
                type="monotone"
                dataKey="frequency" 
                stroke="#8884d8" 
                strokeWidth={2}
                dot={false}
                name="Scenarios"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="chart-legend">
            <p><strong>X-Axis:</strong> Terminal balance ranges</p>
            <p><strong>Y-Axis:</strong> Estimated number of scenarios in each range</p>
            <p><strong>Data:</strong> Smooth distribution based on Monte Carlo statistics and success rate</p>
          </div>
        </div>



        {/* Portfolio Timeline Analysis */}
        <div className="chart-container">
          <h4>Portfolio Survival Timeline</h4>
                      <ResponsiveContainer width="100%" height={450}>
              <BarChart data={enhancedHistoricalData} margin={{ bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="startYear" 
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => value.toString()}
                label={{ value: 'Start Year', position: 'insideBottom', offset: -10 }}
                width={80}
              />
              <YAxis 
                tickFormatter={(value) => `${value} years`}
                width={80}
                domain={[0, request.years]}
                label={{ value: 'Years Survived', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value, name) => [`${value} years`, 'Years Survived']}
                labelFormatter={(label) => {
                  const detail = enhancedHistoricalData.find(d => d.startYear === parseInt(label));
                  if (detail && detail.failed && detail.failureYear) {
                    return `Period: ${label}-${parseInt(label) + request.years - 1} (Failed in ${detail.failureYear})`;
                  }
                  return `Period: ${label}-${parseInt(label) + request.years - 1}`;
                }}
              />
              <Bar 
                dataKey="yearsSurvived" 
                fill="#667eea"
                name="Years Survived"
              >
                {enhancedHistoricalData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`}
                    fill={entry.failed ? '#ff6b6b' : '#667eea'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="chart-legend">
            <p><strong>X-Axis:</strong> Start year of each 30-year historical period</p>
            <p><strong>Y-Axis:</strong> Number of years the portfolio survived (failures show actual years, successes show 30)</p>
            <p><strong>Colors:</strong> 🔴 Red = Failed periods, 🔵 Blue = Successful periods</p>
            <p><strong>Hover:</strong> See exact period, survival years, and failure year (if applicable)</p>
          </div>
        </div>
      </div>

      {/* Metadata */}
      <div className="metadata">
        <h3>ℹ️ Simulation Details</h3>
        <div className="metadata-grid">
          <div className="metadata-item">
            <span className="metadata-label">Data Source:</span>
            <span className="metadata-value">{metadata.dataSource}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Period:</span>
            <span className="metadata-value">{metadata.period}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Timestamp:</span>
            <span className="metadata-value">{new Date(metadata.timestamp).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
